import { CommerceStatusBadge } from "@/components/commerce/commerce-status-badge";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
	type StatusBarItem,
	useWorkspacePage,
} from "@/components/workspace";
import { DeliveryService } from "@/gen/omnios/v1/delivery_connect";
import type { Shipment } from "@/gen/omnios/v1/delivery_pb";
import { AG_GRID_LOCALE_RU } from "@/lib/ag-grid-locale-ru";
import { transport } from "@/lib/grpc";
import { useGridLayout } from "@/lib/use-grid-layout";
import { createClient } from "@connectrpc/connect";

import type {
	ColDef,
	ValueFormatterParams,
} from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";

import { useServerGrid } from "@/lib/use-server-grid";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ChevronDown, Search } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useGridSelection } from "@/hooks/use-grid-selection";

export const Route = createFileRoute("/commerce/shipments/")({
	component: ShipmentsListPage,
});

const client = createClient(DeliveryService, transport);

function ShipmentsListPage() {
	const { t } = useTranslation("commerce");
	const navigate = useNavigate();
	const { onGridReady, onStateChanged } = useGridLayout("commerce-shipments");

	const { selectedRows, hasSelection, onSelectionChanged, ActionBar } =
		useGridSelection<Shipment>();

	const { gridRef, gridProps, totalRows, refreshGrid } = useServerGrid<Shipment>({
		fetchRows: async ({ pageSize, pageToken }) => {
			const resp = await client.listShipments({
				status: "",
				page: { pageSize, pageToken },
			});
			return {
				rows: resp.shipments,
				nextPageToken: resp.page?.nextPageToken,
				totalCount: resp.page?.totalCount ?? 0,
			};
		},
		colToField: {
			orderId: "order_id",
			providerShipmentId: "provider_shipment_id",
			trackingNumber: "tracking_number",
			shippedAt: "shipped_at",
			estimatedDelivery: "estimated_delivery",
		},
	});

	const fmtDate = (ts?: { seconds: bigint }) =>
		ts ? new Date(Number(ts.seconds) * 1000).toLocaleDateString("ru-RU") : "—";

	// ── Column definitions ──
	const columnDefs = useMemo<ColDef<Shipment>[]>(
		() => [
			{
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 36,
				maxWidth: 36,
				pinned: "left",
				suppressHeaderMenuButton: true,
				resizable: false,
			},
			{
				field: "id",
				headerName: "ID",
				width: 100,
				pinned: "left",
				cellClass: "font-mono",
				filter: false,
				valueFormatter: (p: ValueFormatterParams<Shipment>) =>
					p.data?.id ? p.data.id.slice(0, 8) : "",
			},
			{
				field: "orderId",
				headerName: t("pay_col_order"),
				width: 100,
				cellClass: "font-mono",
				valueFormatter: (p: ValueFormatterParams<Shipment>) =>
					p.data?.orderId ? p.data.orderId.slice(0, 8) : "",
			},
			{
				field: "providerShipmentId",
				headerName: t("provider"),
				width: 130,
				valueFormatter: (p: ValueFormatterParams<Shipment>) =>
					p.data?.providerShipmentId || "—",
			},
			{
				field: "trackingNumber",
				headerName: t("tracking_number"),
				width: 160,
				cellClass: "font-mono",
				valueFormatter: (p: ValueFormatterParams<Shipment>) =>
					p.data?.trackingNumber || "—",
			},
			{
				field: "status",
				headerName: t("shipment_status"),
				width: 140,
				filter: false,
				cellRenderer: (p: { value: string }) => {
					if (!p.value) return null;
					return <CommerceStatusBadge entity="shipment" status={p.value} />;
				},
			},
			{
				field: "shippedAt",
				headerName: t("shipped_at"),
				width: 120,
				filter: "agDateColumnFilter",
				valueFormatter: (p: ValueFormatterParams<Shipment>) =>
					fmtDate(p.data?.shippedAt),
			},
			{
				field: "estimatedDelivery",
				headerName: t("est_delivery"),
				width: 120,
				filter: "agDateColumnFilter",
				valueFormatter: (p: ValueFormatterParams<Shipment>) =>
					fmtDate(p.data?.estimatedDelivery),
			},
		],
		[t],
	);

	const defaultColDef = useMemo<ColDef>(
		() => ({
			sortable: true,
			resizable: true,
			suppressHeaderMenuButton: false,
			filter: true,
			filterParams: {
				buttons: ["apply", "reset"],
				closeOnApply: true,
			},
		}),
		[],
	);

	const onRowClicked = useCallback(
		(e: { data: Shipment | undefined }) => {
			if (e.data) {
				navigate({
					to: "/commerce/shipments/$id",
					params: { id: e.data.id },
				});
			}
		},
		[navigate],
	);

	// ── Status bar ──
	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "count",
				content: hasSelection
					? `${selectedRows.length} ${t("common:selected", { defaultValue: "выбрано" })}`
					: `${totalRows} ${t("shipments_count_label", { defaultValue: "отправлений" })}`,
				align: "left" as const,
			},
		],
		[hasSelection, selectedRows.length, totalRows, t],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	return (
		<>
		<div className="flex items-center gap-2 px-4 py-2 shrink-0">
			<div className="relative flex-1">
				<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
				<Input
					placeholder={t("search_placeholder", { defaultValue: "Поиск..." })}
					onChange={(e) =>
						gridRef.current?.api?.setGridOption("quickFilterText", e.target.value)
					}
					className="pl-9 h-9"
				/>
			</div>
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button variant="outline" size="sm" className="h-9">
						{t("common:export", { defaultValue: "Экспорт" })}
						<ChevronDown className="h-3.5 w-3.5 ml-1 opacity-50" />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="end">
					<DropdownMenuItem
						onClick={() =>
							gridRef.current?.api?.exportDataAsCsv({
								fileName: "shipments.csv",
							})
						}
					>
						CSV
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
			<Button
				variant="outline"
				size="sm"
				className="h-9"
				onClick={() => refreshGrid()}
			>
				{t("menu_refresh")}
			</Button>
		</div>
		<div className="flex-1 ag-theme-quartz h-full" style={{ width: "100%" }}>
			<AgGridReact<Shipment>
				ref={gridRef}
				{...gridProps}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				localeText={AG_GRID_LOCALE_RU}
				rowSelection="multiple"
				onSelectionChanged={onSelectionChanged}
				onRowClicked={onRowClicked}
				onGridReady={onGridReady}
				onSortChanged={() => { onStateChanged(); refreshGrid(); }}
				onFilterChanged={() => { onStateChanged(); refreshGrid(); }}
				onColumnMoved={onStateChanged}
				onColumnResized={onStateChanged}
				onColumnVisible={onStateChanged}
				suppressRowClickSelection
				rowClass="cursor-pointer"
				animateRows={false}
				rowHeight={28}
				headerHeight={32}
				getRowId={(p) => p.data?.id}
				overlayNoRowsTemplate={`<div style="text-align:center;padding:40px"><p style="font-size:13px;font-weight:500">${t("no_shipments", { defaultValue: "Нет отправлений" })}</p><p style="font-size:11px;color:hsl(var(--muted-foreground));margin-top:4px">${t("no_shipments_desc", { defaultValue: "Отправления появятся после оформления заказов" })}</p></div>`}
				overlayLoadingTemplate={`<span style="font-size:12px;color:hsl(var(--muted-foreground))">${t("common:loading", { defaultValue: "Загрузка" })}...</span>`}
			/>
		</div>
		<ActionBar />
		</>
	);
}
